/**
 * Helpers: a11y
 * ------------------------------------------------------------------------------
 * Entry file for accessibility utility functions.
 * - Focusables
 * - Keyboard tabbable.
 *
 * @namespace frameA11y
 */
import cssClasses from '~/helpers/cssClasses';

/**
 * Get all focusable elements within targeted container.
 * - Force included parameters defined in config.
 * - Remove excluded elements defined in config.
 * @param {Element} container - Target container to get focusable elements from.
 * @returns {Object}
 */
export function focusable(container, config = {}) {
  const defaults = [
    '[tabindex]',
    '[draggable]',
    'a[href]',
    'area',
    'button:enabled',
    'input:not([type=hidden]):enabled',
    'object',
    'select:enabled',
    'textarea:enabled',
  ];

  /**
   * Override default settings with component configuration.
   */
  if (config && config.include && config.include.length) {
    config.include.forEach((selector) => defaults.push(selector));
  }

  const elements = [...(container.querySelectorAll(defaults.join()))];

  /**
   * Return visible elements from the focusable items.
   */
  const focusableElements = elements
    .filter((element) => {
      return Boolean(
        element.offsetWidth ||
        element.offsetHeight ||
        element.getClientRects().length,
      );
    });

  /**
   * Return un-excluded focusable elements.
   */
  if (config && config.exclude && config.exclude.length) {
    const exclusionList = [...container.querySelectorAll(config.exclude.join())];

    return focusableElements.filter((element) => {
      return !exclusionList.includes(element);
    });
  }

  return focusableElements;
}

/**
 * Set tabindex on focusable elements within target container.
 * @param {Element} container - Target container to get focusable elements from.
 * @param {Integer} tabindex - Tabindex value (Default: 0).
 */
export function updateTabindexOnElement(container, tabindex = 0) {
  const focusableElements = focusable(container);

  if (!focusableElements) {
    throw new Error('Could not find any focusable elements in container');
  }

  focusableElements.forEach((element) => {
    element.setAttribute('tabindex', tabindex);
  });
}

/**
 * Enables focus.tabbable class to body
 * - Used to control styling of accessibility focus states.
 */
export function keyboardTabbable() {

  /**
   * Instantiate state and events.
   */
  function init() {
    setEventListeners();
  }

  /**
   * Set keyboard listeners.
   */
  function setEventListeners() {
    document.addEventListener('keydown', handleKeyboardEvent);
  }

  /**
   * Handle keyboard events after validating Tab.
   * @param {Event} event - Keyboard keydown event.
   */
  function handleKeyboardEvent(event) {
    if (isKeyPressIsTab(event)) {
      handleTabEvent();
    }
  }

  /**
   * Check if Tab key has been pressed.
   * @param {Event} event - Keyboard event passed from user input on document.
   */
  function isKeyPressIsTab(event) {
    return event.keyCode === 9;
  }

  /**
   * Handle Tab keyboard event.
   * @param {Event} event - Key code event set on document.
   */
  function handleTabEvent() {
    document.body.classList.add(cssClasses.tabbable);
    document.removeEventListener('keydown', handleKeyboardEvent);
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
}
